import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Box,
  Button,
  Typography,
  IconButton,
  Paper,
  CardContent,
  Divider,
  CardHeader,
  Card,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { IWorkOrderMapObject } from "../../../apis/OcpwAssetworks/OcpwAssetworksApi";
import AttachDocumentsForm from "./AttachDocumentsForm";

interface WorkOrderPreviewDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  loading?: boolean;
  dtoPreview: IWorkOrderMapObject[][];
  setDtoPreview: any;
}

const WorkOrderPreviewDialog: React.FC<WorkOrderPreviewDialogProps> = ({
  open,
  onClose,
  onConfirm,
  loading = false,
  dtoPreview,
  setDtoPreview,
}) => {
  const [tabIndex, setTabIndex] = useState(0);
  const [pageIndexes, setPageIndexes] = useState<number[]>([]);
  const [attachOpen, setAttachOpen] = useState(false);
  const [missingAttachmentDialogOpen, setMissingAttachmentDialogOpen] = useState(false);
  const [existingWorkOrderDialogOpen, setExistingWorkOrderDialogOpen] = useState(false);
  const [existingWorkOrderObjects, setExistingWorkOrderObjects] = useState<
    { objectIdValue: any; workOrderNumber: any; property: any }[]
  >([]);
  // const [currentWorkOrder, setCurrentWorkOrder] = useState<IWorkOrderMapObject | undefined>(undefined)

  // Reset pageIndexes when dtoPreview or tabIndex changes
  useEffect(() => {
    setPageIndexes(dtoPreview.map(() => 0));
  }, [dtoPreview]);

  // Reset page index to 0 when switching tabs
  useEffect(() => {
    setPageIndexes((prev) =>
      prev.map((page, idx) => (idx === tabIndex ? 0 : page))
    );
  }, [tabIndex]);

  useEffect(()=>{
  const currentLayer = dtoPreview[tabIndex] || [];
  const currentPage = pageIndexes[tabIndex] || 0;
  },[tabIndex,pageIndexes,dtoPreview])

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  const handlePageChange = (direction: "prev" | "next") => {
    setPageIndexes((prev) =>
      prev.map((page, idx) => {
        if (idx !== tabIndex) return page;
        const layerLength = dtoPreview[tabIndex]?.length || 1;
        if (direction === "next") {
          return Math.min(page + 1, layerLength - 1);
        } else {
          return Math.max(page - 1, 0);
        }
      })
    );
  };

  const currentLayer = dtoPreview[tabIndex] || [];
  const currentPage = pageIndexes[tabIndex] || 0;
  const currentWorkOrder = currentLayer[currentPage];

  const collectExistingWorkOrderObjects = () => {
    const allWorkOrders = dtoPreview.flat();
    const existingObjects: {
      objectIdValue: any;
      workOrderNumber: any;
      property: any;
    }[] = [];

    allWorkOrders.forEach((workOrder: any) => {
      const features = workOrder?.features || [];

      features.forEach((feature: any) => {
        const attributes = feature?.attributes;
        const workOrderNumber = attributes?.WorkOrderNumber;
        const objectIdValue = attributes?.OBJECTID;
        const property = attributes?.PropertyName;

        if (workOrderNumber) {
          existingObjects.push({
            objectIdValue,
            workOrderNumber,
            property,
          });
        }
      });
    });

    return existingObjects;
  };

  const getSummaryOfWorkOrderObject = (
    workOrderObject: Record<string, any>
  ) => {
    const { request, features, attachFiles } = workOrderObject;
    if (!request || !features || !features[0]?.attributes) return null;

    // Extract property name for title
    const property = features[0].attributes.PropertyName;

    // Extract phases summary
    const phases =
      Array.isArray(request.phases) && request.phases.length > 0
        ? request.phases.map((phase: any, idx: number) => ({
            idx,
            location: phase.locationCode,
            workCode: phase.craftCode,
            description:phase.description
          }))
        : [];

    // Return attachFiles if present
    return { property, phases, attachFiles };
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>Preview Work Orders</DialogTitle>
        <DialogContent>
          <Tabs
            value={tabIndex}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ mb: 2 }}
          >
            {dtoPreview.map((layer, idx) => (
              <Tab
                key={idx}
                label={`Layer ${idx + 1} (${layer.length} orders)`}
                sx={{ minWidth: 120 }}
              />
            ))}
          </Tabs>
          {currentWorkOrder ? (
            <Box>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mb={2}
              >
                <IconButton
                  onClick={() => handlePageChange("prev")}
                  disabled={currentPage === 0}
                  size="small"
                >
                  <ArrowBackIcon />
                </IconButton>
                <Typography>
                  Order {currentPage + 1} of {currentLayer.length}
                </Typography>
                <IconButton
                  onClick={() => handlePageChange("next")}
                  disabled={currentPage >= currentLayer.length - 1}
                  size="small"
                >
                  <ArrowForwardIcon />
                </IconButton>
              </Box>
              {/* Render summary */}
              {(() => {
                const summary = getSummaryOfWorkOrderObject(currentWorkOrder);
                if (!summary)
                  return <Typography>No summary available.</Typography>;
                return (
                  <Card
                    variant="outlined"
                    sx={{
                      borderRadius: 3,
                      mb: 2,
                      background:
                        "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
                      borderColor: "#90caf9",
                      boxShadow: 4,
                      transition: "box-shadow 0.3s",
                      "&:hover": {
                        boxShadow: 8,
                        borderColor: "#1976d2",
                      },
                    }}
                  >
                    <CardHeader
                      title={
                        <Typography
                          variant="h6"
                          gutterBottom
                          sx={{ textAlign: "center" }}
                        >
                          {summary.property}
                        </Typography>
                      }
                      sx={{ pb: 0 }}
                    />
                    <Divider />
                    <CardContent>
                      <Typography variant="subtitle2" gutterBottom>
                        Phases:
                      </Typography>
                      <Box sx={{ pl: 0, mb: 2 }}>
                        {summary.phases.length > 0 ? (
                          (() => {
                            // Collect all unique property names from all phases that have a value
                            const allKeys = Array.from(
                              new Set(
                                summary.phases.flatMap(
                                  (phase: Record<string, any>) =>
                                    Object.keys(phase).filter(
                                      (key) =>
                                        phase[key] !== null &&
                                        phase[key] !== undefined &&
                                        phase[key] !== ""
                                    )
                                )
                              )
                            );

                            return (
                              <Box
                                component="table"
                                sx={{
                                  width: "100%",
                                  borderCollapse: "collapse",
                                }}
                              >
                                <Box component="thead">
                                  <Box component="tr">
                                    {allKeys.map((key) => (
                                      <Box
                                        component="th"
                                        key={key as React.Key}
                                        sx={{
                                          borderBottom: 1,
                                          borderColor: "divider",
                                          textAlign: "left",
                                          p: 1,
                                          textTransform: "capitalize",
                                        }}
                                      >
                                        {key}
                                      </Box>
                                    ))}
                                  </Box>
                                </Box>
                                <Box component="tbody">
                                  {summary.phases.map(
                                    (
                                      phase: Record<string, any>,
                                      idx: number
                                    ) => (
                                      <Box component="tr" key={idx}>
                                        {allKeys.map((key) => (
                                          <Box
                                            component="td"
                                            key={key as React.Key}
                                            sx={{ p: 1 }}
                                          >
                                            {phase[key as React.Key] !== null &&
                                            phase[key as React.Key] !== undefined &&
                                            phase[key as React.Key] !== ""
                                              ? String(phase[key as React.Key])
                                              : ""}
                                          </Box>
                                        ))}
                                      </Box>
                                    )
                                  )}
                                </Box>
                              </Box>
                            );
                          })()
                        ) : (
                          <Typography variant="body2" sx={{ pl: 1 }}>
                            No phases available.
                          </Typography>
                        )}
                      </Box>
                      <Divider sx={{ my: 1 }} />
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        mt={2}
                      >
                        <Typography variant="subtitle2" mt={2}>
                          Number of features:{" "}
                          {currentWorkOrder.features?.length ?? 0}
                        </Typography>
                        <Button
                          variant="outlined"
                          color="primary"
                          size="small"
                          sx={{ ml: 2, width: "30%" }}
                          onClick={() => {
                            setAttachOpen(true);
                          }}
                        >
                          Attach documents
                        </Button>
                      </Box>
                      {summary.attachFiles &&
                        summary.attachFiles.length > 0 && (
                          <Box mt={2}>
                            <Divider sx={{ my: 1 }} />
                            <Typography variant="subtitle2" gutterBottom>
                              Attached Documents:
                            </Typography>
                            <Box component="ul" sx={{ pl: 3 }}>
                              {summary.attachFiles.map(
                                (file: File, idx: number) => (
                                  <li key={idx}>{file.name}</li>
                                )
                              )}
                            </Box>
                          </Box>
                        )}
                    </CardContent>
                  </Card>
                );
              })()}
            </Box>
          ) : (
            <Typography>No work order data available.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => {
              const existingObjects = collectExistingWorkOrderObjects();

              if (existingObjects.length > 0) {
                setExistingWorkOrderObjects(existingObjects);
                setExistingWorkOrderDialogOpen(true);
                return;
              }

              const allWorkOrders = dtoPreview.flat();
              const missingAttachments = allWorkOrders.filter(
                (wo: any) => !wo.attachFiles || wo.attachFiles.length === 0
              );
              if (missingAttachments.length > 0) {
                setMissingAttachmentDialogOpen(true);
                return;
              }
              onConfirm();
            }}
            color="primary"
            variant="contained"
            disabled={loading}
          >
            Confirm & Submit
          </Button>
        </DialogActions>
        {/* Material UI Dialog for missing attachments */}
        <Dialog
          open={missingAttachmentDialogOpen}
          onClose={() => setMissingAttachmentDialogOpen(false)}
        >
          <DialogTitle>Missing Attachments</DialogTitle>
          <DialogContent>
            <Typography>
              Each work order must have at least one attachment before submitting.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setMissingAttachmentDialogOpen(false)} color="primary">
              OK
            </Button>
          </DialogActions>
        </Dialog>

        {/* Material UI Dialog for existing work orders */}
        <Dialog
          open={existingWorkOrderDialogOpen}
          onClose={() => setExistingWorkOrderDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Existing Work Order Found</DialogTitle>
          <DialogContent>
            <Typography sx={{ mb: 2 }}>
              This work order cannot be submitted because one or more selected
              points already have a work order number.
            </Typography>

            {existingWorkOrderObjects.map((obj, idx) => (
              <Box
                key={idx}
                sx={{
                  border: "1px solid #ddd",
                  borderRadius: 1,
                  p: 2,
                  mb: 1,
                }}
              >
                <Typography>
                  <strong>Work Order Number:</strong> {obj.workOrderNumber}
                </Typography>
                <Typography>
                  <strong>Object ID:</strong> {obj.objectIdValue}
                </Typography>
                <Typography>
                  <strong>Property:</strong> {obj.property}
                </Typography>
              </Box>
            ))}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setExistingWorkOrderDialogOpen(false)}
              color="primary"
            >
              OK
            </Button>
          </DialogActions>
        </Dialog>
      </Dialog>
      <AttachDocumentsForm
        open={attachOpen}
        onClose={() => setAttachOpen(false)}
        setDtoPreview={setDtoPreview}
        tabIndex={tabIndex}
        pageIndexes={pageIndexes}
      />
    </>
  );
};

export default WorkOrderPreviewDialog;
